// embedTokenHandoff — DDIR iframe handoff bootstrap.
//
// The TP backend route /embed/<page> validates a DDIR-minted embed JWT
// (15-minute, single-use) then 302s the iframe to:
//
//   /?page=<page>#token=<sessionJWT>
//
// The session JWT is the standard TP session token (8-hour TTL, same
// shape as the one /api/auth/login mints) and it MUST flow into the
// SPA's localStorage so that AuthContext picks it up via its normal
// boot path. The fragment is the carrier because:
//
//   - Fragments are not sent in the HTTP request to the server (no
//     access-log leak, no Sentry breadcrumb leak, no proxy-log leak)
//   - Fragments are stripped from Referer headers on subresource fetches
//   - Only same-document scripts can read them
//
// After the SPA consumes the fragment, we strip it via history.replaceState
// so the JWT does not linger in browser history.
//
// This module is the pure parser + side-effecty consumer pair that the
// AuthContext useEffect calls once on mount before the localStorage
// check. Pure-function split makes the parser testable in isolation
// without a DOM (see frontend/test/embedTokenHandoff.test.mjs).

const TOKEN_STORAGE_KEY = 'tachoparser_token';

/**
 * Parse a window.location.hash string and return the embed session JWT,
 * or null if no usable token is present.
 *
 * Accepts the input as a string for testability — the AuthContext passes
 * `window.location.hash` at call time.
 *
 * @param {string|null|undefined} hashString  e.g. "#token=abc.def.ghi"
 * @returns {string|null}
 */
export function extractTokenFromHash(hashString) {
  if (!hashString || typeof hashString !== 'string') return null;
  // Strip the leading '#'. URLSearchParams happily parses what's left
  // and handles `&`-separated multi-value fragments uniformly.
  const body = hashString.startsWith('#') ? hashString.slice(1) : hashString;
  if (!body) return null;
  let params;
  try {
    params = new URLSearchParams(body);
  } catch {
    return null;
  }
  const token = params.get('token');
  if (!token) return null;
  // Defence-in-depth: refuse to persist a value that is obviously not a
  // JWT (3 base64url segments). A bad token would be caught by /api/auth/me
  // anyway, but rejecting here keeps junk out of localStorage.
  if (token.split('.').length !== 3) return null;
  return token;
}

/**
 * Pure framedness check: is `win` running inside an iframe? Same-origin
 * frames compare `self !== top` directly; a CROSS-ORIGIN parent (the DDIR
 * embed) can make the `top` access itself throw in some engines — and an
 * exception is only ever possible when a parent exists, so it counts as
 * embedded.
 *
 * Window is passed in (not read from the global) so the function is
 * testable without a DOM — see frontend/test/embedTokenHandoff.test.mjs.
 *
 * @param {Window|null|undefined} win
 * @returns {boolean}
 */
export function computeIsEmbedded(win) {
  if (!win) return false;
  try {
    return win.self !== win.top;
  } catch {
    return true;
  }
}

// Evaluated ONCE at module load — a document's framedness cannot change
// for its lifetime, so there is nothing to re-check per render. The
// typeof guard keeps the module importable in windowless environments
// (node --test).
const IS_EMBEDDED = computeIsEmbedded(typeof window !== 'undefined' ? window : null);

/**
 * True when the SPA is running inside an iframe — in practice the DDIR
 * embed (see module header). Used to hide affordances that make no sense
 * in the embedded chrome (v3.50.4: the sidebar's manual Sign Out — the
 * embed session is DDIR-minted, signing out of it in-place is
 * meaningless). Deliberately NOT used to gate any logout/session-expiry
 * LOGIC: an expired embed session must still clear itself and fall back
 * to the login screen.
 *
 * @returns {boolean}
 */
export function isEmbedded() {
  return IS_EMBEDDED;
}

/**
 * Consume an embed handoff from the given window object: if the hash
 * carries a valid #token=, persist it to localStorage and strip the
 * fragment via history.replaceState. Returns the persisted token, or
 * null if nothing happened.
 *
 * Window is passed in (not read from the global) so the function is
 * testable without a DOM.
 *
 * @param {Window|null|undefined} win
 * @returns {string|null}
 */
export function consumeEmbedHandoff(win) {
  if (!win || !win.location) return null;
  const token = extractTokenFromHash(win.location.hash);
  if (!token) return null;
  try {
    win.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // Quota errors / disabled storage: swallow. AuthContext will
    // re-prompt for login, which is the correct user-visible fallback.
    return null;
  }
  try {
    const url = `${win.location.pathname || '/'}${win.location.search || ''}`;
    win.history.replaceState(null, '', url);
  } catch {
    // History API failure is non-fatal — token is already in storage,
    // user just sees the hash linger in the URL. Don't block boot.
  }
  return token;
}
